// No unit conversion needed - values are stored in user's display unit
import { getPartColor } from './colors';

/**
 * Generate the full HTML string for the shop sheet.
 * Used by both printShopSheet and downloadShopSheet.
 */
function generateShopSheetHTML({
  orderedLayouts,
  currentUnits,
  sheetWidth,
  sheetLength,
  cutWidth,
  getUnitLabel,
  showWatermark = false,
  printFriendly = true,
  modelSnapshot = null,
  orientation = 'auto', // 'auto', 'landscape', 'portrait'
}) {
  // Determine effective orientation
  // Auto: landscape by default, portrait only for nearly-square sheets (ratio <= 1.2)
  // SheetRenderer uses sheetLength as horizontal, sheetWidth as vertical
  let effectiveOrientation = orientation;
  if (orientation === 'auto') {
    // Collect aspect ratios (length/width = horizontal/vertical) from all layouts
    const aspectRatios = orderedLayouts.map(layout => {
      const w = layout.sheetWidth || sheetWidth;
      const l = layout.sheetLength || sheetLength;
      return l / w;
    });
    // Use median aspect ratio for the decision
    const sorted = [...aspectRatios].sort((a, b) => a - b);
    const medianAspect = sorted.length > 0
      ? sorted[Math.floor(sorted.length / 2)]
      : sheetLength / sheetWidth;
    // Portrait only for nearly-square sheets (ratio <= 1.2), landscape otherwise
    effectiveOrientation = medianAspect <= 1.2 ? 'portrait' : 'landscape';
  }
  const isLandscape = effectiveOrientation === 'landscape';
  // Prefer export-ready SVGs (rendered at desktop size) over display SVGs (may be mobile-sized)
  let svgElements = document.querySelectorAll('.shop-sheet-export-container svg');
  if (svgElements.length === 0) {
    svgElements = document.querySelectorAll('.shop-sheet-svg-container svg');
  }
  const svgStrings = Array.from(svgElements).map((svg, layoutIdx) => {
    const clone = svg.cloneNode(true);
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    clone.removeAttribute('style');
    clone.removeAttribute('class');
    clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    clone.style.cssText = 'width: 100%; height: 100%;';

    clone.querySelectorAll('rect, polygon, line').forEach(el => {
      const sw = el.getAttribute('stroke-width');
      if (sw && parseFloat(sw) >= 2) el.setAttribute('stroke-width', '1');
    });

    // Reset dark mode colors to light for print
    const firstRect = clone.querySelector('rect');
    if (firstRect) {
      firstRect.setAttribute('fill', '#FFFFFF');
      firstRect.setAttribute('stroke', '#000000');
    }
    // Reset piece fills and strokes
    clone.querySelectorAll('rect, polygon').forEach((el, i) => {
      if (i === 0) return; // skip background rect
      const fill = el.getAttribute('fill');
      if (fill === '#2a2a2a' || fill === '#3a3a3a' || fill === '#1e1e1e') {
        el.setAttribute('fill', '#FFFFFF');
      }
      const stroke = el.getAttribute('stroke');
      if (stroke === '#666' || stroke === '#444') {
        el.setAttribute('stroke', '#000000');
      }
    });
    // Reset text colors
    clone.querySelectorAll('text').forEach(textEl => {
      const fill = textEl.getAttribute('fill');
      if (fill === '#ddd' || fill === '#999' || fill === '#ccc') {
        textEl.setAttribute('fill', '#000000');
      }
    });

    const layout = orderedLayouts[layoutIdx];
    if (layout) {
      const pieces = layout.cutSequence.filter(cut => cut.type === 'piece');
      const grainArrow = { horizontal: ' ↔', vertical: ' ↕', none: '' };
      // Group by name+dims+grain → same part number
      const partKeyToNum = new Map();
      const nameToPartInfo = new Map();
      let nextNum = 1;
      pieces.forEach(cut => {
        const p = cut.piece;
        const key = `${p.name}|${(p.width || 0).toFixed(4)}|${(p.length || 0).toFixed(4)}|${p.grainDirection}`;
        if (!partKeyToNum.has(key)) {
          partKeyToNum.set(key, { num: nextNum++, grain: p.grainDirection || 'none' });
        }
        const name = p.name || 'Piece';
        if (!nameToPartInfo.has(name)) nameToPartInfo.set(name, []);
        nameToPartInfo.get(name).push(partKeyToNum.get(key));
      });
      const numToLetter = (n) => { let s = ''; while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); } return s; };
      const usedNums = new Map();
      clone.querySelectorAll('text').forEach(textEl => {
        const content = textEl.textContent.trim();
        if (nameToPartInfo.has(content)) {
          const entries = nameToPartInfo.get(content);
          const usedIdx = usedNums.get(content) || 0;
          if (usedIdx < entries.length) {
            const entry = entries[usedIdx];
            textEl.textContent = `${numToLetter(entry.num)}${grainArrow[entry.grain] || ''}`;
            usedNums.set(content, usedIdx + 1);
          }
        }
      });

      // Remove grain lines (horizontal/vertical lines inside pieces)
      clone.querySelectorAll('line').forEach(line => {
        const opacity = line.getAttribute('opacity');
        if (opacity === '1' || opacity === '0.7') {
          line.remove();
        }
      });
    }

    return clone.outerHTML;
  });

  const unitLabel = getUnitLabel();
  const w = sheetWidth.toFixed(1);
  const l = sheetLength.toFixed(1);

  const grainMap = {
    horizontal: '↔ Length',
    vertical: '↕ Width',
    none: '○ None',
  };

  const watermarkHtml = showWatermark
    ? `<div class="watermark">FREE VERSION — CUTCALC.COM<br><span class="wm-sub">Upgrade to Pro to remove watermark</span></div>`
    : '';

  const pages = orderedLayouts.map((layout, sheetIndex) => {
    const pieces = layout.cutSequence.filter(cut => cut.type === 'piece');
    const svgHtml = svgStrings[sheetIndex] || '';

    // Group pieces by name+dims+grain
    const groupedParts = [];
    const partMap = new Map();
    pieces.forEach(cut => {
      const p = cut.piece;
      const key = `${p.name}|${(p.width || 0).toFixed(4)}|${(p.length || 0).toFixed(4)}|${p.grainDirection}`;
      if (!partMap.has(key)) {
        const color = getPartColor(p.originalId || p.id).hex;
        const entry = { num: groupedParts.length + 1, piece: p, qty: 0, color };
        partMap.set(key, entry);
        groupedParts.push(entry);
      }
      partMap.get(key).qty++;
    });

    const half = Math.ceil(groupedParts.length / 2);
    const col1 = groupedParts.slice(0, half);
    const col2 = groupedParts.slice(half);

    const numToLetter2 = (n) => { let s = ''; while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); } return s; };

    const makeRows = (parts) => parts.map(part => {
      const pw = part.piece.width.toFixed(2);
      const pl = part.piece.length.toFixed(2);
      const grain = grainMap[part.piece.grainDirection] || grainMap.none;
      return `<tr>
        <td class="c cn"><span class="pill"${!printFriendly ? ` style="border-color:${part.color};color:${part.color};background:${part.color}20"` : ''}>${numToLetter2(part.num)}</span></td>
        <td class="c cp">${part.piece.name || 'Piece'}</td>
        <td class="c cq">×${part.qty}</td>
        <td class="c cd">${pw}</td>
        <td class="c cd">${pl}</td>
        <td class="c cg">${grain}</td>
      </tr>`;
    }).join('');

    const makeTable = (parts) => parts.length === 0 ? '' : `
      <table class="ct">
        <thead><tr>
          <th class="c cn"></th>
          <th class="c cp">Part</th>
          <th class="c cq">Qty</th>
          <th class="c cd">W</th>
          <th class="c cd">L</th>
          <th class="c cg">Grain</th>
        </tr></thead>
        <tbody>${makeRows(parts)}</tbody>
      </table>`;

    const footerText = showWatermark
      ? ''
      : `<div class="page-footer">Generated by CutCalc • ${new Date().toLocaleDateString()}</div>`;

    return `<div class="page">
      <div class="header">
        <span class="title">Sheet ${sheetIndex + 1} <span class="count">of ${orderedLayouts.length}</span></span>
        <span class="meta">${layout.thicknessLabel ? layout.thicknessLabel + ' · ' : ''}${w} × ${l} ${unitLabel}</span>
      </div>
      <div class="diagram">${svgHtml}</div>
      <div class="bottom">
        <div class="cols">
          <div class="col">${makeTable(col1)}</div>
          ${col2.length > 0 ? `<div class="col">${makeTable(col2)}</div>` : ''}
        </div>
        ${footerText}
      </div>
    </div>`;
  }).join('');

  // Build optional 3D model overview page
  let modelPage = '';
  if (modelSnapshot) {
    const svgData = printFriendly
      ? (modelSnapshot.dataBW || modelSnapshot.data)
      : (modelSnapshot.dataColor || modelSnapshot.data);
    const modelContent = modelSnapshot.type === 'svg'
      ? `<div class="model-view">${svgData}</div>`
      : `<div class="model-view"><img src="${modelSnapshot.data}" alt="3D Model" ${printFriendly ? 'style="filter:grayscale(1)"' : ''}/></div>`;

    modelPage = `<div class="page">
      <div class="header">
        <span class="title">3D Model Overview</span>
        <span class="meta">${orderedLayouts.length} sheet${orderedLayouts.length !== 1 ? 's' : ''} total</span>
      </div>
      <div class="diagram model-diagram">
        ${modelContent}
      </div>
      <div class="bottom">
        <div class="page-footer">Generated by CutCalc • ${new Date().toLocaleDateString()}</div>
      </div>
    </div>`;
  }

  // Page dimensions (letter size with 0.4in margins)
  // Portrait: 8.5" x 11" → content 7.7" x 10.2"
  // Landscape: 11" x 8.5" → content 10.2" x 7.7"
  const pageHeight = isLandscape ? '7.7in' : '10.2in';
  const pageWidth = isLandscape ? '10.2in' : '7.7in';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>CutCalc Shop Sheet</title>
  <style>
    @page { size: letter ${effectiveOrientation}; margin: 0.4in; }
    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif;
      color: #1a1a1a;
      font-size: 8pt;
      line-height: 1.3;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* Each sheet = one full page */
    .page {
      page-break-after: always;
      height: ${pageHeight};
      display: flex;
      flex-direction: column;
    }
    .page:last-child { page-break-after: auto; }

    /* Header — single row */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding-bottom: 4px;
      border-bottom: 1px solid #ccc;
      margin-bottom: 6px;
      flex-shrink: 0;
    }
    .title { font-size: 7.5pt; font-weight: 500; color: #666; }
    .count { color: #999; }
    .meta { font-size: 7.5pt; color: #666; font-weight: 500; }

    /* Diagram — fills all space between header and table */
    .diagram {
      flex: 1;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px 0;
      overflow: hidden;
    }
    .diagram svg {
      width: 100% !important;
      max-height: 100% !important;
      height: auto !important;
      display: block;
      border: 1px solid #ccc;
      border-radius: 3px;
    }

    /* Bottom section — pinned to bottom */
    .bottom {
      flex-shrink: 0;
      margin-top: auto;
    }

    /* Two-column parts */
    .cols {
      display: flex;
      gap: 8px;
    }
    .col { flex: 1; min-width: 0; }

    /* Table */
    .ct {
      width: 100%;
      border-collapse: collapse;
      font-size: 7pt;
      line-height: 1.2;
    }
    .ct thead tr { border-bottom: 1px solid #bbb; }
    .ct thead .c {
      font-weight: 700;
      color: #666;
      text-transform: uppercase;
      font-size: 6pt;
      letter-spacing: 0.3px;
      padding-bottom: 2px;
    }
    .c {
      padding: 2px 4px;
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .cn { width: 20px; font-weight: 600; }
    .pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 13px;
      border-radius: 100px;
      border: 1.5px solid #333;
      font-size: 6pt;
      font-weight: 700;
      color: #333;
    }
    .cq { width: 22px; color: #666; font-weight: 600; }
    .cp { font-weight: 600; max-width: 0; width: 45%; }
    .cd {
      font-family: 'SF Mono', 'Courier New', monospace;
      width: 40px; color: #333; font-size: 6.5pt;
    }
    .cg { width: 55px; font-size: 6pt; color: #555; }
    .ct tbody tr:nth-child(even) { background: #f8f8f8; }
    .ct tbody tr { border-bottom: 1px solid #f0f0f0; }

    /* Footer */
    .page-footer {
      text-align: center;
      font-size: 6pt;
      color: #bbb;
      padding-top: 4px;
      margin-top: 4px;
      border-top: 1px solid #eee;
    }
    .watermark { text-align: center; font-size: 12pt; font-weight: 600; color: #ccc; padding: 8px; }
    .wm-sub { font-size: 7pt; font-weight: 400; color: #bbb; }

    /* 3D Model overview page */
    .model-diagram {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .model-view {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .model-view svg {
      width: 100%;
      max-height: 100%;
      height: auto;
    }
    .model-view img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    /* Screen preview */
    @media screen {
      body { max-width: ${isLandscape ? '10.5in' : '7.5in'}; margin: 0.5in auto; padding: 0 0.25in; background: #F4F7F5; }
      .page {
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 6px;
        padding: 0.35in;
        margin-bottom: 0.5in;
        height: auto;
        min-height: ${isLandscape ? '450px' : '600px'};
        box-shadow: 0 1px 4px rgba(0,0,0,0.08);
      }
      .diagram { min-height: ${isLandscape ? '200px' : '300px'}; }
    }
  </style>
</head>
<body>
  ${modelPage}
  ${pages}
  ${watermarkHtml}
  <script>window.onload = function() { window.print(); };</script>
</body>
</html>`;

  return html;
}

/**
 * Opens a new window with a print-optimized shop sheet.
 * One sheet per page. SVG centered in space above table. Table pinned to bottom.
 */
export function printShopSheet(options) {
  const html = generateShopSheetHTML(options);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}

/**
 * Downloads the shop sheet as a standalone HTML file.
 * Same content as printShopSheet but without auto-print, saved as a file.
 */
export function downloadShopSheet(options) {
  const html = generateShopSheetHTML(options);
  const filename = `cutcalc-shop-sheet-${new Date().toISOString().slice(0, 10)}.html`;

  // Remove auto-print, add print + download buttons
  const downloadHtml = html
    .replace(
      '<script>window.onload = function() { window.print(); };</script>',
      `<div style="position:fixed;top:12px;right:12px;z-index:999;display:flex;flex-direction:column;gap:6px;" class="no-print">
        <button id="download-btn" style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:#e27c39;color:white;border:none;border-radius:6px;cursor:pointer;" title="Download as HTML file">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </button>
        <button onclick="window.print()" style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:#e27c39;color:white;border:none;border-radius:6px;cursor:pointer;" title="Print shop sheet">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        </button>
      </div>
      <style>@media print { .no-print { display: none !important; } }</style>
      <script>
        document.getElementById('download-btn').addEventListener('click', function() {
          var blob = new Blob([document.documentElement.outerHTML], { type: 'text/html' });
          var a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = '${filename}';
          a.click();
          URL.revokeObjectURL(a.href);
        });
      </script>`
    );

  // Open preview window first, then user can download from there
  const previewWindow = window.open('', '_blank');
  if (previewWindow) {
    previewWindow.document.write(downloadHtml);
    previewWindow.document.close();
  }
}

/**
 * Downloads the shop sheet as a PDF file (Pro feature - server-side generation).
 * Sends the HTML to the server for Puppeteer-based PDF generation.
 *
 * @param {Object} options - Same options as generateShopSheetHTML
 * @param {string} licenseKey - User's Pro license key
 * @param {Function} onProgress - Progress callback: 'generating', 'downloading', 'complete', 'error'
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function downloadShopSheetPDF(options, licenseKey, onProgress = () => {}) {
  // Use local dev server (port 3001) when running on localhost:3000, otherwise use relative URL for Vercel
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const API_BASE = (isLocalDev && window.location.port === '3000') ? 'http://localhost:3001' : '';
  const GENERATE_API = `${API_BASE}/api/generate-shopsheet`;

  try {
    onProgress('generating');

    // Generate the HTML content (without watermark for Pro users, no auto-print)
    const htmlContent = generateShopSheetHTML({
      ...options,
      showWatermark: false,
      printFriendly: true,
    }).replace(
      '<script>window.onload = function() { window.print(); };</script>',
      '' // Remove auto-print script
    );

    // Determine orientation from layouts — landscape unless nearly square
    let orientation = 'landscape';
    if (options.orderedLayouts?.length > 0) {
      const firstLayout = options.orderedLayouts[0];
      const w = firstLayout.sheetWidth || options.sheetWidth;
      const l = firstLayout.sheetLength || options.sheetLength;
      orientation = (l / w) <= 1.2 ? 'portrait' : 'landscape';
    }

    // Send to server for PDF generation
    const response = await fetch(GENERATE_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        licenseKey,
        htmlContent,
        orientation,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 403) {
        onProgress('error');
        return {
          success: false,
          error: 'Pro license required',
          checkoutUrl: errorData.checkoutUrl,
        };
      }

      throw new Error(errorData.message || `Server error: ${response.status}`);
    }

    onProgress('downloading');

    // Download the PDF blob
    const pdfBlob = await response.blob();
    const filename = `cutcalc-shop-sheet-${new Date().toISOString().slice(0, 10)}.pdf`;

    // Trigger download
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    onProgress('complete');
    return { success: true };

  } catch (error) {
    console.error('PDF download error:', error);
    onProgress('error');
    return {
      success: false,
      error: error.message || 'Failed to generate PDF',
    };
  }
}
